import { useState } from "react";
import { Flex, Spinner, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { INovoVinculoCobranca } from "@/models/consignado/proposal-billings.model";
import { buscarNovosVinculos } from "@/services/consignado/proposta-cobranca.service";
import { removeMask } from "@/utils/removeMask";
import NovosVinculosAccordion from "../components/NovosVinculos/NovosVinculosAccordion";
import CollapsibleSection from "./CollapsibleSection";
import { useCobrancaDetalhe } from "@/hooks/useCobrancaDetalhe";

type Props = {
  // UUID da cobrança (`cobranca_id` da listagem).
  cobrancaId: string | null;
  tomadorDocumento: string;
};

// Seção "Novos Vínculos" do detalhe da CCB — REAL, igual à tela legada:
// GET /proposta/cobrancas/novos-vinculos?documento=<CPF do tomador> lista os
// vínculos novos; o accordion (renegociação: simular / gerar proposta /
// averbar) precisa de id_proposta, competências, taxa e status de negociação
// da cobrança, que vêm do detalhe (useCobrancaDetalhe). Nasce colapsada e só
// dispara as queries na 1ª abertura.
export default function NovosVinculosSection({ cobrancaId, tomadorDocumento }: Props) {
  const [aberta, setAberta] = useState(false);
  const documento = removeMask(tomadorDocumento ?? "");

  const { data: cobranca, isLoading: isLoadingDetalhe, isError } = useCobrancaDetalhe(cobrancaId, aberta);

  const { data: novosVinculos, isLoading: vinculosCarregando } = useQuery<INovoVinculoCobranca[]>({
    queryKey: ["novos-vinculos", documento],
    queryFn: () => buscarNovosVinculos(documento),
    enabled: aberta && !!documento,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const conteudo =
    isLoadingDetalhe || vinculosCarregando ? (
      <Flex justifyContent="center" py={4}>
        <Spinner color="primary" />
      </Flex>
    ) : isError || !cobranca ? (
      <Text fontSize="sm" color="gray.500">
        Não foi possível carregar os novos vínculos desta CCB.
      </Text>
    ) : (
      <NovosVinculosAccordion
        data={novosVinculos ?? []}
        proposalId={cobranca.id_proposta}
        numeroCcb={cobranca.numero_ccb}
        competenciasResumo={cobranca.competencias_resumo ?? []}
        taxaJurosAtual={cobranca.taxa_juros}
        etapaNegociacaoStatus={cobranca.etapaNegociacaoStatus}
        negociacaoDados={cobranca.negociacaoDados}
      />
    );

  return (
    <CollapsibleSection
      title="Novos Vinculos"
      defaultExpanded={false}
      onToggle={(expanded) => expanded && setAberta(true)}
    >
      {aberta && conteudo}
    </CollapsibleSection>
  );
}
